import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Label } from "@/components/ui/Input";
import { HubTouchSelect } from "@/components/ui/HubTouchSelect";
import { HubSettingsSection } from "@/components/settings/HubSettingsSection";
import { useHubToast } from "@/components/ui/HubToast";
import { ReadOnlyHoverLayer } from "@/components/ui/title-help";

export type PersonAccessSystem = {
  id: number;
  name: string;
  kind: string;
  isStandard: boolean;
  active: boolean;
};

export type PersonAccessGrant = {
  id: number;
  systemId: number;
  systemName: string;
  kind: string;
  accountLabel: string | null;
  grantedOn: string;
  revokedOn: string | null;
  notes: string | null;
};

type PersonAccessSectionProps = {
  staffId: number;
  grants: PersonAccessGrant[];
  systems: PersonAccessSystem[];
  writesAllowed: boolean;
  readOnlyHint: string;
};

export function PersonAccessSection({
  staffId,
  grants,
  systems,
  writesAllowed,
  readOnlyHint,
}: PersonAccessSectionProps) {
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const [systemId, setSystemId] = useState("");

  const openGrants = grants.filter((grant) => grant.revokedOn == null);
  const closedGrants = grants.filter((grant) => grant.revokedOn != null);
  const openSystemIds = new Set(openGrants.map((grant) => grant.systemId));
  const addable = systems.filter((system) => !openSystemIds.has(system.id));

  async function refresh() {
    await queryClient.invalidateQueries({ queryKey: ["people", "staff", staffId] });
    await queryClient.invalidateQueries({ queryKey: ["people", "directory"] });
  }

  const grantMutation = useMutation({
    mutationFn: () =>
      apiFetch(`/api/people/staff/${staffId}/grants`, {
        method: "POST",
        body: { systemId: Number(systemId) },
      }),
    onSuccess: async () => {
      setSystemId("");
      await refresh();
    },
    onError: (error: Error) => showToast({ message: error.message, tone: "error" }),
  });

  const standardMutation = useMutation({
    mutationFn: () =>
      apiFetch<{ added: number }>(`/api/people/staff/${staffId}/grants/standard`, { method: "POST" }),
    onSuccess: async (result) => {
      showToast({
        message: result.added === 0 ? "Standard apps are already on this person." : `Added ${result.added} standard apps.`,
        tone: "success",
      });
      await refresh();
    },
    onError: (error: Error) => showToast({ message: error.message, tone: "error" }),
  });

  const revokeMutation = useMutation({
    mutationFn: (grantId: number) => apiFetch(`/api/people/grants/${grantId}/revoke`, { method: "POST" }),
    onSuccess: async () => {
      await refresh();
    },
    onError: (error: Error) => showToast({ message: error.message, tone: "error" }),
  });

  return (
    <>
      <HubSettingsSection
        title="Onboarding access"
        hoverHint="Record each app or login this person is given. Add standard apps copies the current standard list onto this person and skips anything they already have. Changing the catalog later does not add or remove access for people who already have a grant."
      >
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[14rem] flex-1">
            <Label htmlFor="people-grant-system">App</Label>
            <HubTouchSelect
              id="people-grant-system"
              className="mt-1"
              aria-label="App"
              value={systemId}
              onChange={setSystemId}
              disabled={!writesAllowed}
              options={[
                { value: "", label: "Choose an app" },
                ...addable.map((system) => ({
                  value: String(system.id),
                  label: system.isStandard ? `${system.name} (standard)` : system.name,
                })),
              ]}
            />
          </div>
          <ReadOnlyHoverLayer active={!writesAllowed} hoverHint={readOnlyHint}>
            <Button
              type="button"
              disabled={!writesAllowed || systemId === "" || grantMutation.isPending}
              onClick={() => grantMutation.mutate()}
            >
              Add app
            </Button>
          </ReadOnlyHoverLayer>
          <ReadOnlyHoverLayer active={!writesAllowed} hoverHint={readOnlyHint}>
            <Button
              type="button"
              variant="secondary"
              disabled={!writesAllowed || standardMutation.isPending}
              onClick={() => standardMutation.mutate()}
            >
              Add standard apps
            </Button>
          </ReadOnlyHoverLayer>
        </div>
      </HubSettingsSection>

      <HubSettingsSection
        title="Offboarding checklist"
        hoverHint="Each open app is one item to close when this person leaves. Mark removed when that login is disabled. Closed items stay in the history below so a later catalog change does not hide what they once had."
      >
        {openGrants.length === 0 ? (
          <p className="text-sm text-slate-600">No open apps. There is nothing to remove.</p>
        ) : (
          <ul className="divide-y divide-slate-200 rounded-md border border-slate-200">
            {openGrants.map((grant) => (
              <li key={grant.id} className="flex flex-wrap items-center justify-between gap-3 px-3 py-2">
                <div>
                  <p className="text-sm font-medium text-slate-900">{grant.systemName}</p>
                  <p className="text-xs text-slate-600">
                    Granted {grant.grantedOn}
                    {grant.accountLabel ? ` · ${grant.accountLabel}` : ""}
                  </p>
                </div>
                <ReadOnlyHoverLayer active={!writesAllowed} hoverHint={readOnlyHint}>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    disabled={!writesAllowed || revokeMutation.isPending}
                    onClick={() => revokeMutation.mutate(grant.id)}
                  >
                    Mark removed
                  </Button>
                </ReadOnlyHoverLayer>
              </li>
            ))}
          </ul>
        )}
        {closedGrants.length > 0 ? (
          <table className="mt-4 min-w-full text-sm">
            <thead className="border-b border-slate-200 text-left text-xs font-medium uppercase text-slate-500">
              <tr>
                <th className="py-2 pr-3">Removed</th>
                <th className="py-2 pr-3">App</th>
                <th className="py-2 pr-3">Account</th>
                <th className="py-2">Granted</th>
              </tr>
            </thead>
            <tbody>
              {closedGrants.map((grant) => (
                <tr key={grant.id} className="border-b border-slate-100">
                  <td className="py-2 pr-3 text-slate-700">{grant.revokedOn}</td>
                  <td className="py-2 pr-3 text-slate-800">{grant.systemName}</td>
                  <td className="py-2 pr-3 text-slate-700">{grant.accountLabel ?? "—"}</td>
                  <td className="py-2 text-slate-700">{grant.grantedOn}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </HubSettingsSection>
    </>
  );
}
